import type {
  CraftableWeapon,
  CraftingSocketKey,
  WeaponConfiguration,
} from "@/lib/crafting/types";
import { SOCKET_LABELS } from "@/lib/crafting/types";
import { findComponent } from "@/lib/crafting/engine";
import { assetUrl } from "@/lib/bungie/assets";

export interface CraftingSlotBarProps {
  weapon: CraftableWeapon;
  planned: WeaponConfiguration;
  current?: WeaponConfiguration;
  activeKey: CraftingSocketKey;
  currentLevel: number;
  onActivate: (key: CraftingSocketKey) => void;
}

/**
 * The horizontal socket row.
 *
 * Intrinsic sits apart in a diamond, exactly as the game frames it; the
 * remaining sockets are squares. A slot whose planned plug differs from the
 * weapon's real one gets a "changed" marker so current-versus-planned is
 * legible at a glance without opening anything.
 *
 * Implemented as a tablist: arrow keys move between slots, matching the
 * controller's left/right navigation.
 */
export function CraftingSlotBar({
  weapon,
  planned,
  current,
  activeKey,
  currentLevel,
  onActivate,
}: CraftingSlotBarProps) {
  const intrinsic = weapon.sockets.find((s) => s.key === "intrinsic");
  const rest = weapon.sockets.filter((s) => s.key !== "intrinsic");
  const order = weapon.sockets.map((s) => s.key);

  function onKeyDown(event: React.KeyboardEvent) {
    const delta = event.key === "ArrowRight" ? 1 : event.key === "ArrowLeft" ? -1 : 0;
    if (!delta) return;
    event.preventDefault();
    const i = order.indexOf(activeKey);
    const next = order[(i + delta + order.length) % order.length];
    if (next) onActivate(next);
  }

  return (
    <div
      role="tablist"
      aria-label="Weapon components"
      onKeyDown={onKeyDown}
      className="flex items-center gap-3 overflow-x-auto bg-band px-4 py-3 sm:gap-5 sm:px-6"
    >
      {intrinsic && (
        <>
          <Slot
            weapon={weapon}
            socketKey="intrinsic"
            planned={planned}
            current={current}
            active={activeKey === "intrinsic"}
            currentLevel={currentLevel}
            onActivate={onActivate}
            shape="diamond"
          />
          <span aria-hidden className="h-12 w-px shrink-0 bg-line-dim" />
        </>
      )}

      <div className="flex items-center gap-2 sm:gap-3">
        {rest.map((socket) => (
          <Slot
            key={socket.key}
            weapon={weapon}
            socketKey={socket.key}
            planned={planned}
            current={current}
            active={activeKey === socket.key}
            currentLevel={currentLevel}
            onActivate={onActivate}
            shape={socket.key === "memento" ? "dashed" : "square"}
          />
        ))}
      </div>

    </div>
  );
}

interface SlotProps {
  weapon: CraftableWeapon;
  socketKey: CraftingSocketKey;
  planned: WeaponConfiguration;
  current?: WeaponConfiguration;
  active: boolean;
  currentLevel: number;
  onActivate: (key: CraftingSocketKey) => void;
  shape: "diamond" | "square" | "dashed";
}

function Slot({
  weapon,
  socketKey,
  planned,
  current,
  active,
  currentLevel,
  onActivate,
  shape,
}: SlotProps) {
  const component = findComponent(weapon, socketKey, planned[socketKey]);
  const equippedHash = current?.[socketKey];
  const changed = equippedHash !== undefined && equippedHash !== planned[socketKey];
  const locked = component ? component.requiredLevel > currentLevel : false;
  const icon = assetUrl(component?.icon);
  const label = SOCKET_LABELS[socketKey];

  const isDiamond = shape === "diamond";
  // The diamond's frame is an inner element, not the button. Rotating the button
  // would leave its layout box unchanged while drawing ~1.41x wider, which spills
  // out of the bar — and since `overflow-x-auto` forces `overflow-y` to `auto`,
  // that spill became a scrollbar. The inner square's diagonal fits the button.
  const size = isDiamond ? "h-16 w-16 sm:h-20 sm:w-20" : "h-14 w-14 sm:h-16 sm:w-16";

  return (
    <button
      type="button"
      role="tab"
      id={`slot-${socketKey}`}
      aria-selected={active}
      aria-controls={`panel-${socketKey}`}
      tabIndex={active ? 0 : -1}
      onClick={() => onActivate(socketKey)}
      className={[
        "relative grid shrink-0 place-items-center transition-colors duration-150 ease-(--ease-crisp)",
        size,
        isDiamond ? "" : "border",
        shape === "dashed" ? "border-dashed border-line-dim" : "border-line-dim",
        isDiamond
          ? ""
          : active
            ? "border-line bg-white/14"
            : "bg-white/[0.03] hover:bg-white/8",
      ].join(" ")}
    >
      {isDiamond && (
        <span
          aria-hidden
          className={[
            "pointer-events-none absolute h-11 w-11 rotate-45 border transition-colors duration-150 sm:h-14 sm:w-14",
            active ? "border-line bg-white/14" : "border-line-dim bg-white/[0.03]",
          ].join(" ")}
        />
      )}
      <span className="relative">
        {component ? (
          <span className="relative grid h-9 w-9 place-items-center overflow-hidden rounded-full ring-1 ring-white/25 sm:h-11 sm:w-11">
            {icon ? (
              <img src={icon} alt="" aria-hidden className="h-full w-full object-cover" />
            ) : (
              <span aria-hidden className="h-full w-full bg-perk-deep" />
            )}
            {component.enhanced && (
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0 rounded-full"
                style={{
                  background:
                    "linear-gradient(to top, var(--color-enhanced) 0%, color-mix(in srgb, var(--color-enhanced-hi) 55%, transparent) 38%, transparent 68%)",
                }}
              />
            )}
          </span>
        ) : (
          <span aria-hidden className="text-xs text-ink-3">
            Empty
          </span>
        )}
      </span>

      {/* Screen-reader description of the whole slot. */}
      <span className="sr-only">
        {label}: {component ? component.name : "empty"}
        {component?.enhanced ? ", enhanced" : ""}
        {locked ? `, locked until level ${component?.requiredLevel}` : ""}
        {changed ? ", changed from the current configuration" : ""}
      </span>

      {/* Badges sit inside the button bounds — hanging them outside overflowed
          the bar and produced a scrollbar. */}
      {changed && (
        <span
          aria-hidden
          title="Changed from current"
          className="absolute top-0.5 right-0.5 grid h-3 w-3 place-items-center rounded-full bg-enhanced text-[0.5rem] font-bold text-void"
        >
          ●
        </span>
      )}
      {locked && (
        <span
          aria-hidden
          className="absolute bottom-0.5 left-0.5 grid h-3.5 min-w-3.5 place-items-center bg-void/90 px-0.5 text-[0.5rem] font-semibold text-level"
        >
          🔒
        </span>
      )}
    </button>
  );
}
